import { findRecordsByModelName } from '../YhcEstimateFormFrontPage/findRecordsByModelName'
import {
  addDaysYmd,
  type YhcMasterVersionPeriod,
} from '../YhcEstimateFormFrontPage/masterVersionByDate'
import { parseAdditionalLeadTimeDays } from '../YhcEstimateFormFrontPage/regularWorkingDaysCalc'
import { formatRevisionDateForNote } from './serviceLevelRevisionUtils'

export type MasterVersionSpanUncertainSide = 'pickup' | 'delivery'
export type MasterVersionSpanNoteKind = 'leadTime' | 'timeBand' | 'workingDays'

export type ZipSpanFields = {
  prefCode?: string | null
  timeBand?: string | null
  regularWorkingDay?: string | null
  additionalLeadTimeDaysFrom?: unknown
  additionalLeadTimeDaysTo?: unknown
}

export type PrefSpanFields = {
  sizeRank0NeedDays?: unknown
  sizeRank1NeedDays?: unknown
}

export type VersionedSpanMasters = {
  zipFrom: ZipSpanFields | null
  zipTo: ZipSpanFields | null
  prefFromTo: PrefSpanFields | null
}

export type MasterVersionSpanChangeFlags = {
  leadTimeE: boolean
  leadTimeFg: boolean
  timeBand: boolean
  workingDays: boolean
}

export type MasterVersionSpanNotePeriod = 'after' | 'before'

export type MasterVersionSpanDiff = {
  uncertainSide: MasterVersionSpanUncertainSide
  leadTimeEDate: string | null
  leadTimeFgDate: string | null
  timeBandDate: string | null
  workingDaysDate: string | null
  /** 注釈が付いた時点の、画面に出している側の時間帯コード。空文字はレコードなし */
  timeBandDisplayedCode: string | null
  /** 注釈が付いた時点の、次版の時間帯コード。空文字はレコードなし */
  timeBandOtherCode: string | null
  workingDaysDisplayedPattern: string | null
  workingDaysOtherPattern: string | null
}

export type MasterVersionSpanNotes = {
  uncertainSide: MasterVersionSpanUncertainSide | null
  leadTimeENote: string | null
  leadTimeFgNote: string | null
  timeBandNote: string | null
  timeBandPeriod: MasterVersionSpanNotePeriod | null
  timeBandPeriodLabel: string | null
  /** ツールチップに出す時間帯コード。null はツールチップなし。空文字は指定不可 */
  timeBandTooltipCode: string | null
  /** お届け日選択で、お預かり側カードを変更後コードに差し替える */
  timeBandDisplayOverrideCode: string | null
  workingDaysNote: string | null
  workingDaysPeriod: MasterVersionSpanNotePeriod | null
  workingDaysPeriodLabel: string | null
  workingDaysRevisionDate: string | null
  workingDaysTooltipPattern: string | null
  workingDaysTooltipAsOf: string | null
  workingDaysDisplayOverridePattern: string | null
  workingDaysDisplayOverrideAsOf: string | null
  workingDaysDisplayedPattern: string | null
  workingDaysOtherPattern: string | null
}

const ZIP_SPAN_FIELDS = [
  'id',
  'prefCode',
  'timeBand',
  'regularWorkingDay',
  'additionalLeadTimeDaysFrom',
  'additionalLeadTimeDaysTo',
]

const PREF_SPAN_FIELDS = ['id', 'sizeRank0NeedDays', 'sizeRank1NeedDays']

const toYmd = (value: string | null | undefined): string => (value || '').slice(0, 10)

/**
 * 期間開始日の翌日〜終了日までに有効開始するバージョンの日付（YYYY-MM-DD）
 */
export const findMasterRevisionDatesInRange = (
  versions: Array<{ enabledFrom: string }>,
  rangeStart: string,
  rangeEnd: string,
): string[] => {
  const start = toYmd(rangeStart)
  const end = toYmd(rangeEnd)
  if (!start || !end || start > end) {
    return []
  }
  const dates = versions
    .map((v) => toYmd(v.enabledFrom))
    .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d) && d > start && d <= end)
  return [...new Set(dates)].sort()
}

export const findVersionIdCoveringDate = (
  versions: Array<{ id?: string; enabledFrom: string; disabledAt?: string }>,
  ymd: string,
): string | undefined => {
  const day = toYmd(ymd)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined
  const covering = versions.filter((v) => {
    const from = toYmd(v.enabledFrom)
    const until = toYmd(v.disabledAt)
    return !!v.id && from && until && from <= day && until >= day
  })
  covering.sort((a, b) => toYmd(b.enabledFrom).localeCompare(toYmd(a.enabledFrom)))
  return covering[0]?.id
}

export const findVersionIdStartingOnDate = (
  versions: Array<{ id?: string; enabledFrom: string }>,
  ymd: string,
): string | undefined => {
  const day = toYmd(ymd)
  const matched = versions.filter((v) => !!v.id && toYmd(v.enabledFrom) === day)
  matched.sort((a, b) => toYmd(b.enabledFrom).localeCompare(toYmd(a.enabledFrom)))
  return matched[0]?.id
}

export const versionEnabledFromYmd = (
  versions: Array<{ id?: string; enabledFrom: string }>,
  versionId: string | undefined,
): string => {
  if (!versionId) return ''
  const matched = versions.find((v) => v.id === versionId)
  return toYmd(matched?.enabledFrom)
}

/**
 * 画面に出している版の有効開始が、改定日より前ならその改定を注釈対象にする。
 * 版の開始日が取れないときは抑制しない。
 */
export const isRevisionAfterDisplayedVersion = (
  displayedEnabledFrom: string,
  revisionDate: string,
): boolean => {
  const displayed = toYmd(displayedEnabledFrom)
  const revision = toYmd(revisionDate)
  if (!displayed || !revision) return true
  return displayed < revision
}

export const zipMasterRecordId = (versionId: string, zip: string): string =>
  `${versionId}--${zip}`

export const prefFromToRecordId = (
  versionId: string,
  fromPref: string,
  toPref: string,
): string => `${versionId}--${fromPref}-${toPref}`

const normalizeTimeBand = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value)

const normalizeWorkingDayPattern = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value).trim()

const normalizeNeedDays = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return ''
  const n = Number(value)
  return Number.isFinite(n) ? String(n) : String(value)
}

/** 次版の有効開始日の前日。before 側の「まで」に使う */
const formatDayBeforeRevisionForNote = (revisionDate: string): string | null => {
  const dayBefore = addDaysYmd(revisionDate, -1)
  if (!dayBefore) return null
  return formatRevisionDateForNote(dayBefore)
}

export const formatMasterVersionSpanPeriodLabel = (
  revisionDate: string,
  period: MasterVersionSpanNotePeriod,
): string | null => {
  if (!revisionDate) return null
  if (period === 'before') {
    const dayBeforeLabel = formatDayBeforeRevisionForNote(revisionDate)
    if (!dayBeforeLabel) return null
    return `${dayBeforeLabel}まで`
  }
  return `${formatRevisionDateForNote(revisionDate)}以降`
}

export const formatMasterVersionSpanNote = (
  revisionDate: string,
  kind: MasterVersionSpanNoteKind,
  uncertainSide?: MasterVersionSpanUncertainSide,
  period: MasterVersionSpanNotePeriod = 'after',
): string | null => {
  if (!revisionDate) return null
  const dateLabel = formatRevisionDateForNote(revisionDate)
  if (kind === 'leadTime') {
    return `※${dateLabel}よりマスタが改訂されます。リードタイムが異なります`
  }
  if (!uncertainSide) return null
  const sideLabel = uncertainSide === 'pickup' ? 'お預かり側' : 'お届け側'
  if (period === 'before') {
    const dayBeforeLabel = formatDayBeforeRevisionForNote(revisionDate)
    if (!dayBeforeLabel) return null
    if (kind === 'timeBand') {
      return `※${dayBeforeLabel}まではマスタが異なります。${sideLabel}の時間帯が異なります`
    }
    return `※${dayBeforeLabel}まではマスタが異なります。${sideLabel}の営業日が異なります`
  }
  if (kind === 'timeBand') {
    return `※${dateLabel}よりマスタが改訂されます。${sideLabel}の時間帯が異なります`
  }
  return `※${dateLabel}よりマスタが改訂されます。${sideLabel}の営業日が異なります`
}

export const combineRevisionNotes = (
  ...notes: Array<string | null | undefined>
): string => {
  return notes
    .filter((note): note is string => !!note && note.trim().length > 0)
    .join(' ')
}

const hasSpanValue = (value: string | null | undefined): boolean =>
  !!value && value.trim().length > 0

export const emptyMasterVersionSpanDiff = (
  uncertainSide: MasterVersionSpanUncertainSide,
): MasterVersionSpanDiff => ({
  uncertainSide,
  leadTimeEDate: null,
  leadTimeFgDate: null,
  timeBandDate: null,
  workingDaysDate: null,
  timeBandDisplayedCode: null,
  timeBandOtherCode: null,
  workingDaysDisplayedPattern: null,
  workingDaysOtherPattern: null,
})

export const emptyMasterVersionSpanNotes = (): MasterVersionSpanNotes => ({
  uncertainSide: null,
  leadTimeENote: null,
  leadTimeFgNote: null,
  timeBandNote: null,
  timeBandPeriod: null,
  timeBandPeriodLabel: null,
  timeBandTooltipCode: null,
  timeBandDisplayOverrideCode: null,
  workingDaysNote: null,
  workingDaysPeriod: null,
  workingDaysPeriodLabel: null,
  workingDaysRevisionDate: null,
  workingDaysTooltipPattern: null,
  workingDaysTooltipAsOf: null,
  workingDaysDisplayOverridePattern: null,
  workingDaysDisplayOverrideAsOf: null,
  workingDaysDisplayedPattern: null,
  workingDaysOtherPattern: null,
})

const spanPeriodForOtherValue = (
  uncertainSide: MasterVersionSpanUncertainSide,
  revisionDate: string | null,
  otherValue: string | null,
): MasterVersionSpanNotePeriod | null => {
  if (!revisionDate) return null
  if (uncertainSide === 'pickup' && hasSpanValue(otherValue)) return 'before'
  return 'after'
}

export const buildMasterVersionSpanNotesFromDiff = (
  diff: MasterVersionSpanDiff | null | undefined,
): MasterVersionSpanNotes => {
  if (!diff) return emptyMasterVersionSpanNotes()
  const timeBandPeriod = spanPeriodForOtherValue(
    diff.uncertainSide,
    diff.timeBandDate,
    diff.timeBandOtherCode ?? null,
  )
  const workingDaysPeriod = spanPeriodForOtherValue(
    diff.uncertainSide,
    diff.workingDaysDate,
    diff.workingDaysOtherPattern ?? null,
  )
  const timeBandTooltipCode = !diff.timeBandDate
    ? null
    : timeBandPeriod === 'before'
      ? (diff.timeBandDisplayedCode ?? '')
      : (diff.timeBandOtherCode ?? '')
  const workingDaysTooltipPattern = !diff.workingDaysDate
    ? null
    : workingDaysPeriod === 'before'
      ? (diff.workingDaysDisplayedPattern ?? '')
      : (diff.workingDaysOtherPattern ?? '')
  const workingDaysTooltipAsOf = !diff.workingDaysDate
    ? null
    : workingDaysPeriod === 'before'
      ? addDaysYmd(diff.workingDaysDate, -1) || diff.workingDaysDate
      : diff.workingDaysDate
  return {
    uncertainSide: diff.uncertainSide,
    leadTimeENote: formatMasterVersionSpanNote(diff.leadTimeEDate || '', 'leadTime'),
    leadTimeFgNote: formatMasterVersionSpanNote(diff.leadTimeFgDate || '', 'leadTime'),
    timeBandNote: timeBandPeriod
      ? formatMasterVersionSpanNote(
          diff.timeBandDate || '',
          'timeBand',
          diff.uncertainSide,
          timeBandPeriod,
        )
      : null,
    timeBandPeriod,
    timeBandPeriodLabel: timeBandPeriod
      ? formatMasterVersionSpanPeriodLabel(diff.timeBandDate || '', timeBandPeriod)
      : null,
    timeBandTooltipCode,
    timeBandDisplayOverrideCode:
      timeBandPeriod === 'before' ? diff.timeBandOtherCode || null : null,
    workingDaysNote: workingDaysPeriod
      ? formatMasterVersionSpanNote(
          diff.workingDaysDate || '',
          'workingDays',
          diff.uncertainSide,
          workingDaysPeriod,
        )
      : null,
    workingDaysPeriod,
    workingDaysPeriodLabel: workingDaysPeriod
      ? formatMasterVersionSpanPeriodLabel(diff.workingDaysDate || '', workingDaysPeriod)
      : null,
    workingDaysRevisionDate: diff.workingDaysDate,
    workingDaysTooltipPattern,
    workingDaysTooltipAsOf,
    workingDaysDisplayOverridePattern:
      workingDaysPeriod === 'before' ? diff.workingDaysOtherPattern || null : null,
    workingDaysDisplayOverrideAsOf:
      workingDaysPeriod === 'before' ? diff.workingDaysDate : null,
    workingDaysDisplayedPattern: diff.workingDaysDate
      ? (diff.workingDaysDisplayedPattern ?? '')
      : null,
    workingDaysOtherPattern: diff.workingDaysDate
      ? (diff.workingDaysOtherPattern ?? '')
      : null,
  }
}

const pickUncertainZip = (
  masters: VersionedSpanMasters,
  uncertainSide: MasterVersionSpanUncertainSide,
): ZipSpanFields | null => (uncertainSide === 'pickup' ? masters.zipFrom : masters.zipTo)

export type MasterVersionSpanCompareContext = {
  displayed?: VersionedSpanMasters
  pickupEnabledFrom?: string
  deliveryEnabledFrom?: string
  revisionDate?: string
}

export const compareMasterVersionSpanPair = (
  start: VersionedSpanMasters,
  next: VersionedSpanMasters,
  uncertainSide: MasterVersionSpanUncertainSide,
  zipFrom: string,
  zipTo: string,
  context: MasterVersionSpanCompareContext = {},
): MasterVersionSpanChangeFlags => {
  const flags: MasterVersionSpanChangeFlags = {
    leadTimeE: false,
    leadTimeFg: false,
    timeBand: false,
    workingDays: false,
  }
  const displayed = context.displayed || start
  const revisionDate = context.revisionDate || ''
  const pickupEnabledFrom = context.pickupEnabledFrom || ''
  const deliveryEnabledFrom = context.deliveryEnabledFrom || ''
  const uncertainEnabledFrom =
    uncertainSide === 'pickup' ? pickupEnabledFrom : deliveryEnabledFrom
  const sideNotesOpen =
    !revisionDate ||
    isRevisionAfterDisplayedVersion(uncertainEnabledFrom, revisionDate)

  const uncertainZip = uncertainSide === 'pickup' ? zipFrom : zipTo
  if (uncertainZip && sideNotesOpen) {
    const displayedZip = pickUncertainZip(displayed, uncertainSide)
    const nextZip = pickUncertainZip(next, uncertainSide)
    if (!displayedZip || !nextZip) {
      flags.timeBand = true
      flags.workingDays = true
    } else {
      flags.timeBand =
        normalizeTimeBand(displayedZip.timeBand) !==
        normalizeTimeBand(nextZip.timeBand)
      flags.workingDays =
        normalizeWorkingDayPattern(displayedZip.regularWorkingDay) !==
        normalizeWorkingDayPattern(nextZip.regularWorkingDay)
    }
  }

  if (zipFrom && zipTo) {
    if (!next.zipFrom || !next.zipTo) {
      flags.leadTimeE = true
      flags.leadTimeFg = true
    } else {
      const fromOpen =
        !revisionDate ||
        isRevisionAfterDisplayedVersion(pickupEnabledFrom, revisionDate)
      const toOpen =
        !revisionDate ||
        isRevisionAfterDisplayedVersion(deliveryEnabledFrom, revisionDate)
      let additionalChanged = false
      if (fromOpen) {
        additionalChanged =
          !displayed.zipFrom ||
          parseAdditionalLeadTimeDays(displayed.zipFrom.additionalLeadTimeDaysFrom) !==
            parseAdditionalLeadTimeDays(next.zipFrom.additionalLeadTimeDaysFrom)
      }
      if (toOpen && !additionalChanged) {
        additionalChanged =
          !displayed.zipTo ||
          parseAdditionalLeadTimeDays(displayed.zipTo.additionalLeadTimeDaysTo) !==
            parseAdditionalLeadTimeDays(next.zipTo.additionalLeadTimeDaysTo)
      }
      if (!start.prefFromTo || !next.prefFromTo) {
        flags.leadTimeE = true
        flags.leadTimeFg = true
      } else {
        flags.leadTimeE =
          additionalChanged ||
          normalizeNeedDays(start.prefFromTo.sizeRank0NeedDays) !==
            normalizeNeedDays(next.prefFromTo.sizeRank0NeedDays)
        flags.leadTimeFg =
          additionalChanged ||
          normalizeNeedDays(start.prefFromTo.sizeRank1NeedDays) !==
            normalizeNeedDays(next.prefFromTo.sizeRank1NeedDays)
      }
    }
  }

  return flags
}

export const applySpanChangesAtDate = (
  diff: MasterVersionSpanDiff,
  revisionDate: string,
  flags: MasterVersionSpanChangeFlags,
): MasterVersionSpanDiff => {
  return {
    ...diff,
    uncertainSide: diff.uncertainSide,
    leadTimeEDate: diff.leadTimeEDate || (flags.leadTimeE ? revisionDate : null),
    leadTimeFgDate: diff.leadTimeFgDate || (flags.leadTimeFg ? revisionDate : null),
    timeBandDate: diff.timeBandDate || (flags.timeBand ? revisionDate : null),
    workingDaysDate: diff.workingDaysDate || (flags.workingDays ? revisionDate : null),
  }
}

const captureUncertainSideValues = (
  diff: MasterVersionSpanDiff,
  revisionDate: string,
  masters: VersionedSpanMasters,
  uncertainSide: MasterVersionSpanUncertainSide,
  displayed: VersionedSpanMasters,
  flags: MasterVersionSpanChangeFlags,
  timeBandWasUnset: boolean,
  workingDaysWasUnset: boolean,
): MasterVersionSpanDiff => {
  let next = diff
  if (flags.timeBand && timeBandWasUnset && next.timeBandDate === revisionDate) {
    const displayedZip = pickUncertainZip(displayed, uncertainSide)
    const otherZip = pickUncertainZip(masters, uncertainSide)
    next = {
      ...next,
      timeBandDisplayedCode: normalizeTimeBand(displayedZip?.timeBand),
      timeBandOtherCode: otherZip ? normalizeTimeBand(otherZip.timeBand) : '',
    }
  }
  if (flags.workingDays && workingDaysWasUnset && next.workingDaysDate === revisionDate) {
    const displayedZip = pickUncertainZip(displayed, uncertainSide)
    const otherZip = pickUncertainZip(masters, uncertainSide)
    next = {
      ...next,
      workingDaysDisplayedPattern: normalizeWorkingDayPattern(
        displayedZip?.regularWorkingDay,
      ),
      workingDaysOtherPattern: otherZip
        ? normalizeWorkingDayPattern(otherZip.regularWorkingDay)
        : '',
    }
  }
  return next
}

export type MasterVersionSpanDisplayContext = {
  displayed: VersionedSpanMasters
  pickupEnabledFrom: string
  deliveryEnabledFrom: string
}

export const buildMasterVersionSpanDiffFromVersionedMasters = (
  start: VersionedSpanMasters,
  nextByRevisionDate: Array<{ revisionDate: string; masters: VersionedSpanMasters }>,
  uncertainSide: MasterVersionSpanUncertainSide,
  zipFrom: string,
  zipTo: string,
  display?: MasterVersionSpanDisplayContext,
): MasterVersionSpanDiff => {
  let diff = emptyMasterVersionSpanDiff(uncertainSide)
  const shown = display?.displayed || start
  for (const { revisionDate, masters } of nextByRevisionDate) {
    const flags = compareMasterVersionSpanPair(
      start,
      masters,
      uncertainSide,
      zipFrom,
      zipTo,
      {
        displayed: display?.displayed,
        pickupEnabledFrom: display?.pickupEnabledFrom,
        deliveryEnabledFrom: display?.deliveryEnabledFrom,
        revisionDate,
      },
    )
    const timeBandWasUnset = !diff.timeBandDate
    const workingDaysWasUnset = !diff.workingDaysDate
    diff = applySpanChangesAtDate(diff, revisionDate, flags)
    diff = captureUncertainSideValues(
      diff,
      revisionDate,
      masters,
      uncertainSide,
      shown,
      flags,
      timeBandWasUnset,
      workingDaysWasUnset,
    )
  }
  return diff
}

const recordsById = <T extends { id?: string }>(records: T[]): Map<string, T> => {
  const map = new Map<string, T>()
  for (const record of records) {
    if (record?.id) map.set(record.id, record)
  }
  return map
}

const uniqueIds = (ids: Array<string | undefined>): string[] => [
  ...new Set(ids.filter((id): id is string => !!id)),
]

const zipFromMap = (
  map: Map<string, ZipSpanFields & { id?: string }>,
  versionId: string | undefined,
  zip: string,
): ZipSpanFields | null => {
  if (!versionId || !zip) return null
  return map.get(zipMasterRecordId(versionId, zip)) ?? null
}

const prefIdForZips = (
  versionId: string | undefined,
  zipFrom: ZipSpanFields | null,
  zipTo: ZipSpanFields | null,
): string | undefined => {
  const fromPref = (zipFrom?.prefCode || '').trim()
  const toPref = (zipTo?.prefCode || '').trim()
  if (!versionId || !fromPref || !toPref) return undefined
  return prefFromToRecordId(versionId, fromPref, toPref)
}

export const resolveMasterVersionSpanRange = (
  dateMode: 'pickup' | 'delivery',
  pickupSelectedDate: string | null | undefined,
  pickupDateResetFlag: boolean,
  deliverySelectedDate: string | null | undefined,
  deliveryDateResetFlag: boolean,
): {
  start: string
  end: string
  uncertainSide: MasterVersionSpanUncertainSide
} | null => {
  if (dateMode === 'delivery') {
    if (!deliverySelectedDate || deliveryDateResetFlag) return null
    const start = addDaysYmd(deliverySelectedDate, -14)
    if (!start) return null
    return { start, end: deliverySelectedDate.slice(0, 10), uncertainSide: 'pickup' }
  }
  if (!pickupSelectedDate || pickupDateResetFlag) return null
  const end = addDaysYmd(pickupSelectedDate, 14)
  if (!end) return null
  return { start: pickupSelectedDate.slice(0, 10), end, uncertainSide: 'delivery' }
}

export const fetchAndCompareMasterVersionSpan = async (input: {
  versions: YhcMasterVersionPeriod[]
  rangeStart: string
  rangeEnd: string
  uncertainSide: MasterVersionSpanUncertainSide
  zipFrom: string
  zipTo: string
  versionPickup?: string
  versionDelivery?: string
}): Promise<MasterVersionSpanDiff> => {
  const zipFrom = (input.zipFrom || '').trim()
  const zipTo = (input.zipTo || '').trim()
  const revisionDates = findMasterRevisionDatesInRange(
    input.versions,
    input.rangeStart,
    input.rangeEnd,
  )
  const empty = emptyMasterVersionSpanDiff(input.uncertainSide)
  if (!revisionDates.length) return empty

  const startVersionId = findVersionIdCoveringDate(input.versions, input.rangeStart)
  if (!startVersionId) return empty

  const displayedPickupVersionId = input.versionPickup || startVersionId
  const displayedDeliveryVersionId = input.versionDelivery || startVersionId

  const nextVersionIds = revisionDates.map((date) =>
    findVersionIdStartingOnDate(input.versions, date),
  )
  const comparable = revisionDates
    .map((revisionDate, i) => ({ revisionDate, versionId: nextVersionIds[i] }))
    .filter((row): row is { revisionDate: string; versionId: string } => !!row.versionId)
  if (!comparable.length) return empty

  const zipIds = uniqueIds([
    zipFrom ? zipMasterRecordId(startVersionId, zipFrom) : undefined,
    zipTo ? zipMasterRecordId(startVersionId, zipTo) : undefined,
    zipFrom ? zipMasterRecordId(displayedPickupVersionId, zipFrom) : undefined,
    zipTo ? zipMasterRecordId(displayedDeliveryVersionId, zipTo) : undefined,
    ...comparable.flatMap(({ versionId }) => [
      zipFrom ? zipMasterRecordId(versionId, zipFrom) : undefined,
      zipTo ? zipMasterRecordId(versionId, zipTo) : undefined,
    ]),
  ])

  const zipRecords: Array<ZipSpanFields & { id?: string }> = zipIds.length
    ? await findRecordsByModelName('yhc_by_zipcodes', {
        fields: ZIP_SPAN_FIELDS,
        filter: { id: { _in: zipIds } },
        limit: -1,
      })
    : []
  const zipMap = recordsById(zipRecords)

  const startMastersBase = {
    zipFrom: zipFromMap(zipMap, startVersionId, zipFrom),
    zipTo: zipFromMap(zipMap, startVersionId, zipTo),
  }
  const nextMastersBase = comparable.map(({ revisionDate, versionId }) => ({
    revisionDate,
    versionId,
    zipFrom: zipFromMap(zipMap, versionId, zipFrom),
    zipTo: zipFromMap(zipMap, versionId, zipTo),
  }))

  const prefIds = uniqueIds([
    prefIdForZips(startVersionId, startMastersBase.zipFrom, startMastersBase.zipTo),
    ...nextMastersBase.map((row) => prefIdForZips(row.versionId, row.zipFrom, row.zipTo)),
  ])
  const prefRecords: Array<PrefSpanFields & { id?: string }> = prefIds.length
    ? await findRecordsByModelName('yhc_pref_from_to_infos', {
        fields: PREF_SPAN_FIELDS,
        filter: { id: { _in: prefIds } },
        limit: -1,
      })
    : []
  const prefMap = recordsById(prefRecords)

  const prefOf = (id: string | undefined): PrefSpanFields | null =>
    (id && prefMap.get(id)) || null

  const start: VersionedSpanMasters = {
    ...startMastersBase,
    prefFromTo: prefOf(
      prefIdForZips(startVersionId, startMastersBase.zipFrom, startMastersBase.zipTo),
    ),
  }
  const displayed: VersionedSpanMasters = {
    zipFrom: zipFromMap(zipMap, displayedPickupVersionId, zipFrom),
    zipTo: zipFromMap(zipMap, displayedDeliveryVersionId, zipTo),
    prefFromTo: start.prefFromTo,
  }
  const nextByRevisionDate = nextMastersBase.map((row) => ({
    revisionDate: row.revisionDate,
    masters: {
      zipFrom: row.zipFrom,
      zipTo: row.zipTo,
      prefFromTo: prefOf(prefIdForZips(row.versionId, row.zipFrom, row.zipTo)),
    },
  }))

  return buildMasterVersionSpanDiffFromVersionedMasters(
    start,
    nextByRevisionDate,
    input.uncertainSide,
    zipFrom,
    zipTo,
    {
      displayed,
      pickupEnabledFrom: versionEnabledFromYmd(
        input.versions,
        displayedPickupVersionId,
      ),
      deliveryEnabledFrom: versionEnabledFromYmd(
        input.versions,
        displayedDeliveryVersionId,
      ),
    },
  )
}
